import { extractPolicyNumberFromText } from "./voicePoliciesApi";
import { inferIncidentTypeFromNarrative, matchIncidentTypeFromSpeech } from "./incidentTypeOptions";

export interface TranscriptTurn {
  speaker: string;
  text: string;
  time?: string;
  streaming?: boolean;
}

export interface ClaimField {
  label: string;
  value: string;
  status: string;
  source: string;
}

export interface ClaimEvent {
  tone?: string;
  title: string;
  detail: string;
  rule?: string;
  time?: string;
}

export interface IntakeState {
  route?: string;
  progress?: number;
  fields: Record<string, ClaimField>;
  transcript: TranscriptTurn[];
  events: ClaimEvent[];
  handoff: Record<string, string>;
  packet_markdown?: string;
  model?: string;
}

export const INITIAL_FIELD_GROUPS: {
  title: string;
  fields: [string, string][];
}[] = [
  {
    title: "Claim details",
    fields: [
      ["policyType", "Policy Type"],
      ["policyNumber", "Policy number"],
      ["locationType", "Location Type"],
      ["incidentType", "Incident Type"],
      ["date", "Date"],
      ["time", "Time"],
      ["incidentLocation", "Incident location"],
      ["incidentDescription", "Incident description"],
    ],
  },
];

export const ROUTE_LABELS: Record<string, string> = {
  emergency_escalation: "Emergency escalation",
  needs_docs: "Needs documents",
  special_investigation: "Special investigation",
  ready_for_adjuster: "Ready for adjuster",
};

/**
 * REST base for voice FNOL / live agent (Java {@code voiceForms} in smart-ims-insu-backend-app).
 * Call paths like {@code /api/sessions} are appended to this origin.
 */
export function getVoiceApiOrigin(): string {
  const fromEnv =
    process.env.REACT_APP_VOICE_FORMS_API_URL || deriveVoiceFormsApiUrlFromBase();
  // Legacy Python Insurance Claim Live Agent Team (live_demo/server.py, e.g. port 8002):
  // const python = process.env.REACT_APP_CHAT_GEMINI_LIVE_URL;
  // return (python || "http://127.0.0.1:8002").replace(/\/$/, "");
  return fromEnv.replace(/\/$/, "");
}

function deriveVoiceFormsApiUrlFromBase(): string {
  const base = process.env.REACT_APP_BASE_URL;
  if (base) {
    return `${base.replace(/\/$/, "")}/voiceForms`;
  }
  return "http://localhost:8080/smartims/1.0/api/voiceForms";
  // Legacy Python (no Java voiceForms path):
  // return (process.env.REACT_APP_CHAT_GEMINI_LIVE_URL || "http://127.0.0.1:8002").replace(/\/$/, "");
}

/**
 * WebSocket base for Gemini live voice. Connect with {@code `${getVoiceWsOrigin()}/ws/live`}.
 * Java maps this to {@code /smartims/1.0/api/voiceForms/ws/live}.
 */
export function getVoiceWsOrigin(): string {
  const fromEnv = process.env.REACT_APP_VOICE_FORMS_WS_URL;
  if (fromEnv) return fromEnv.replace(/\/$/, "");
  // Legacy Python: same host as REACT_APP_CHAT_GEMINI_LIVE_URL with ws scheme, path /ws/live
  // return (process.env.REACT_APP_CHAT_GEMINI_LIVE_URL || "http://127.0.0.1:8002")
  //   .replace(/^http/, "ws")
  //   .replace(/\/$/, "");
  return getVoiceApiOrigin().replace(/^http/, "ws");
}

export function nowTime(): string {
  return new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const FIELD_LABELS: Record<string, string> = Object.fromEntries(
  INITIAL_FIELD_GROUPS.flatMap((group) => group.fields)
);

const LEGACY_FIELD_IDS: Record<string, string> = {
  policy: "policyNumber",
  type: "incidentType",
  location: "incidentLocation",
  description: "incidentDescription",
};

function isMissingField(field?: ClaimField): boolean {
  return !field || field.status === "missing";
}

const WEAK_FIELD_VALUES = new Set([
  "other",
  "not specified",
  "unknown",
  "n/a",
  "none",
  "touch with",
  "touch",
  "instance",
  "example",
  "excuse me",
  "excuse",
]);

function isWeakFieldValue(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return !normalized || WEAK_FIELD_VALUES.has(normalized);
}

const INCIDENT_LOCATION_STOP_WORDS = new Set([
  "hello",
  "hi",
  "hey",
  "thanks",
  "yes",
  "no",
  "okay",
  "ok",
  "commercial",
  "auto",
  "home",
  "renters",
  "renter",
  "policy",
  "help",
  "safe",
  "something",
  "else",
  "calling",
  "report",
  "loss",
  "rental",
  "property",
  "instance",
  "example",
  "excuse me",
  "excuse",
  "estimate",
  "amount",
  "total",
  "residence",
  "residential",
  "workplace",
  "highway",
  "somewhere",
  "elsewhere",
  "personal auto",
  "personal do",
  "homeowners",
]);

const POLICY_TYPE_PHRASES =
  /\b(?:personal\s+auto|homeowners?|home\s+owner|automobile|auto\s+policy|home\s+policy)\b/i;

function looksLikePolicyNumberSpeech(text: string): boolean {
  const trimmed = text.trim();
  if (/^\d{2,8}$/.test(trimmed)) return true;
  const lower = text.toLowerCase();
  if (
    /\b(?:first|last|starts?|starting|ends?|beginning|letters?|characters?|digits?|numbers?)\b/.test(
      lower
    ) &&
    /\b[A-Z]{1,5}-?[A-Z0-9]{2,}\b/i.test(text)
  ) {
    return true;
  }
  return (
    /\b(?:starting|starts?)\s+(?:number|with)?\s*[A-Za-z0-9]{2,}/i.test(text) ||
    /\b(?:i\s+)?know\s+(?:it\s+)?starts?\s+(?:with\s+)?[A-Za-z0-9]{2,}/i.test(text)
  );
}

function isNarrativeOrDatetimeSpeech(text: string): boolean {
  const lower = text.toLowerCase();
  if (text.length > 48) return true;
  if (extractDateFromText(text)) return true;
  if (extractTimeFromText(text)) return true;
  if (/\b\d{1,2}:\d{2}\s*(?:a\.?m\.?|p\.?m\.?)\b/i.test(text)) return true;
  if (/\b(19|20)\d{2}\b/.test(text) && text.length > 12) return true;
  if (/\b(?:i\s+)?saw\b/.test(lower)) return true;
  if (/\b(?:caught\s+on\s+fire|on\s+fire|house\s+caught|happened|witnessed)\b/i.test(lower)) {
    return true;
  }
  if (/\bit'?s\b/i.test(lower) && /\d/.test(text)) return true;
  return false;
}

function shouldOverwriteIncidentLocation(
  current: string | undefined,
  next: string
): boolean {
  if (!current?.trim()) return true;
  if (!next.trim()) return false;
  if (isNarrativeOrDatetimeSpeech(next) && !isNarrativeOrDatetimeSpeech(current)) {
    return false;
  }
  if (
    current.length <= 35 &&
    next.length > current.length + 20 &&
    isNarrativeOrDatetimeSpeech(next)
  ) {
    return false;
  }
  return next.length <= current.length + 25;
}

function isPlausibleIncidentLocation(value: string): boolean {
  const text = value.trim().replace(/\.$/, "");
  const lower = text.toLowerCase();
  if (!text || text.length > 80 || INCIDENT_LOCATION_STOP_WORDS.has(lower)) return false;
  if (POLICY_TYPE_PHRASES.test(text) || looksLikePolicyNumberSpeech(text)) return false;
  if (isNarrativeOrDatetimeSpeech(text)) return false;
  if (/\b(estimate|amount|total|accident|insurance|policy)\b/i.test(lower)) {
    return false;
  }
  if (/\b(street|avenue|ave|road|rd|blvd|drive|dr|lane|ln|way|court|ct)\b/i.test(text)) {
    return true;
  }
  if (/\d/.test(text)) {
    if (/\b(19|20)\d{2}\b/.test(text) || /\b\d{1,2}:\d{2}/.test(text)) return false;
    return /\b(?:street|#|number)\b/i.test(text) || /street\s+number/i.test(text);
  }
  if (/street\s+number/i.test(text)) return true;
  if (/^[A-Za-z][a-z]+(?:\s+[A-Za-z][a-z]+)+$/.test(text)) return true;
  return text.length >= 4 && /^[A-Za-z][a-z]+(?:\s+[A-Za-z][a-z]+)?$/.test(text);
}

function isBadIncidentDescription(
  value: string,
  claimantLines: string[] = []
): boolean {
  const v = value.trim();
  if (!v) return true;
  const lower = v.toLowerCase();
  if (
    /initiated contact with a greeting/i.test(v) ||
    /did not provide any details/i.test(v) ||
    /user has not provided/i.test(v)
  ) {
    return true;
  }
  if (/\bhello\b/.test(lower) && /\bfile a claim\b/.test(lower)) return true;
  if (claimantLines.length >= 2) {
    const joined = claimantLines
      .map((line) => line.trim().toLowerCase())
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ");
    const normalized = lower.replace(/\s+/g, " ");
    if (joined.length >= 30 && normalized.length >= joined.length * 0.85) {
      if (
        normalized.includes(joined.slice(0, Math.min(50, joined.length))) ||
        joined.includes(normalized.slice(0, Math.min(50, normalized.length)))
      ) {
        return true;
      }
    }
  }
  return false;
}

function extractDescriptionFromSingleTurn(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length < 12) return null;
  const lower = trimmed.toLowerCase();

  if (
    /^(?:hello|hi|hey|file a claim|homeowners?|auto policy|yes|no|ok(?:ay)?|thanks)\.?$/i.test(
      trimmed
    )
  ) {
    return null;
  }

  if (/^\d{2,8}$/.test(trimmed)) return null;
  if (POLICY_TYPE_PHRASES.test(trimmed) && trimmed.length < 40) return null;

  const fromClause = extractIncidentDescriptionFromNarrative(trimmed);
  if (fromClause) return fromClause;

  if (
    /\b(fire|smoke|flames|flood|stolen|collision|crash|hit|damage|burning)\b/i.test(
      lower
    )
  ) {
    const sentences = trimmed
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length >= 12);
    const incidentSentence = sentences.find((s) =>
      /\b(fire|smoke|flames|flood|stolen|collision|crash|hit|damage|burning|clear)\b/i.test(
        s
      )
    );
    if (incidentSentence) return compactNarrativeText(incidentSentence, 280);

    const fireClause = trimmed.match(
      /[^.?!]*\b(?:fire|smoke|flames|flood|stolen|collision|crash|damage|burning)\b[^.?!]*/i
    );
    if (fireClause) {
      const clause = fireClause[0]
        .replace(/^(?:.*?\b(?:on|at)\s+)?\d{1,2}(?:st|nd|rd|th)?\s+\w+\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s*/i, "")
        .replace(/^(?:.*?\b(?:policy|homeowners?|file a claim)\b\s*)+/i, "")
        .trim();
      if (clause.length >= 12) return compactNarrativeText(clause, 280);
    }

    if (trimmed.length <= 120 && !/\b(?:file a claim|hello|policy)\b/i.test(lower)) {
      return compactNarrativeText(trimmed, 280);
    }
  }

  if (/\bran\s+into\s+another\s+car\b/i.test(lower)) return "Ran into another car";
  if (/\byou\s+ran\s+into\b/i.test(lower) && /\banother\s+car\b/i.test(lower)) {
    return "You ran into another car";
  }
  if (/\bcar\s+ran\s+into\b/i.test(lower)) return "A car ran into yours";
  if (/\b(?:house|home).{0,40}caught\s+on\s+fire\b/i.test(lower)) {
    return "House caught on fire";
  }

  return null;
}

function extractDescriptionFromClaimantTurns(
  turns: TranscriptTurn[]
): string | null {
  let best: string | null = null;
  for (const turn of turns) {
    if (turn.speaker !== "Claimant" || !turn.text?.trim() || turn.streaming) {
      continue;
    }
    const desc = extractDescriptionFromSingleTurn(turn.text);
    if (desc) best = desc;
  }
  return best;
}

function mergeIncomingField(
  target: Record<string, ClaimField>,
  id: string,
  incoming?: ClaimField,
  claimantLines: string[] = []
) {
  if (!incoming || isMissingField(incoming) || isWeakFieldValue(incoming.value)) {
    return;
  }
  if (
    id === "incidentDescription" &&
    isBadIncidentDescription(incoming.value, claimantLines)
  ) {
    return;
  }
  const current = target[id];
  if (
    id === "incidentDescription" &&
    current &&
    !isMissingField(current) &&
    !isBadIncidentDescription(current.value, claimantLines) &&
    isBadIncidentDescription(incoming.value, claimantLines)
  ) {
    return;
  }
  const callerAnswered =
    incoming.source === "live transcript" && incoming.status === "complete";
  const replacePolicyFromCaller =
    callerAnswered &&
    (id === "policyType" || id === "policyNumber") &&
    current &&
    !isMissingField(current) &&
    current.source === "customer policies";
  if (
    id === "incidentLocation" &&
    current &&
    !isMissingField(current) &&
    !shouldOverwriteIncidentLocation(current.value, incoming.value)
  ) {
    return;
  }
  if (
    replacePolicyFromCaller ||
    !current ||
    isMissingField(current) ||
    isWeakFieldValue(current.value) ||
    incoming.status === "complete" ||
    incoming.status === "urgent"
  ) {
    target[id] = {
      ...incoming,
      label: FIELD_LABELS[id] || incoming.label,
    };
  }
}

/** Maps backend/legacy field ids to the React claim form schema. */
export function mergeClaimFields(
  base: Record<string, ClaimField>,
  incoming: Record<string, ClaimField>,
  claimantLines: string[] = []
): Record<string, ClaimField> {
  const merged = { ...base };
  for (const [id, field] of Object.entries(incoming)) {
    mergeIncomingField(merged, id, field, claimantLines);
  }
  return merged;
}

export function getIntakeTranscriptText(state: IntakeState): string {
  return state.transcript.map((turn) => turn.text).join(" ");
}

const AGENT_BOILERPLATE =
  /^(i can start the claim|hello\.?\s*i'?m here to help|first,?\s*(?:are you|could you tell))/i;

function isAgentBoilerplate(text: string): boolean {
  return AGENT_BOILERPLATE.test(text.trim());
}

/** Agent listing account policy types — not the caller's answer. */
function isAgentPolicyOptionList(text: string): boolean {
  const lower = text.toLowerCase();
  if (/\bon your account i see\b/i.test(lower)) return true;
  if (/\bwhich (?:one )?applies\b/i.test(lower)) return true;
  if (/\bwhat type of policy\b/i.test(lower) && /\bor\b/i.test(lower)) return true;
  if (
    /\b(?:personal\s+auto|homeowners?|umbrella)\b/i.test(lower) &&
    /\s+or\s+/i.test(lower) &&
    /\b(?:see|which|what type)\b/i.test(lower)
  ) {
    return true;
  }
  return false;
}

function isAgentLocationOptionList(text: string): boolean {
  const lower = text.toLowerCase();
  if (!/\?/.test(text)) return false;
  return /\b(?:primary\s+residence|secondary\s+home|something\s+else|highway|parking\s+lot|workplace|or\s+other)\b/i.test(
    lower
  );
}

/** Only parse agent echoes of caller answers — not option lists in questions. */
function extractAgentConfirmation(text: string): Record<string, string> {
  const values: Record<string, string> = {};
  const trimmed = text.trim();
  const lower = trimmed.toLowerCase();
  if (!trimmed || isAgentBoilerplate(trimmed) || isAgentLocationOptionList(trimmed)) {
    return values;
  }

  const isOptionList = isAgentPolicyOptionList(trimmed);

  if (
    !isOptionList &&
    (/\bgot it,?\s*homeowners?\b/i.test(trimmed) ||
      /\bgot it,?\s*home\s*owners?\b/i.test(trimmed) ||
      /\b(?:home\s*owner'?s?|homeowners?)\s+policy\b/i.test(lower) ||
      /\b(?:home\s*owner'?s?|homeowners?)\s+policy\s+listed\b/i.test(lower) ||
      /\bhomeowners?\s+policy,?\s*(?:got it|okay|ok|understood)\b/i.test(lower))
  ) {
    values.policyType = "Homeowners";
  } else if (
    !isOptionList &&
    (/\bgot it,?\s*(?:personal\s+)?auto\b/i.test(trimmed) ||
      /\b(?:personal\s+)?auto(?:\s+policy)?\s+listed\b/i.test(lower) ||
      (/\b(?:an?\s+)?auto(?:\s+policy)?\b/i.test(lower) &&
        /\b(?:okay|understand|understood|got it)\b/i.test(lower) &&
        !/\bhomeowners?\b/i.test(lower)))
  ) {
    values.policyType = "Auto";
  } else if (/\b(?:your\s+)?home,?\s*(?:got it|okay|ok|understood)\b/i.test(trimmed)) {
    values.policyType = "Homeowners";
  } else if (
    /\b(?:an?\s+)?auto(?:\s+policy)?,?\s*(?:got it|okay|ok|understood)\b/i.test(trimmed)
  ) {
    values.policyType = "Auto";
  }

  return values;
}

export function transcriptHasClaimantAnswer(
  turns: TranscriptTurn[],
  minLength = 2
): boolean {
  return turns.some(
    (t) =>
      t.speaker === "Claimant" &&
      !t.streaming &&
      (t.text?.trim().length ?? 0) >= minLength
  );
}

const COACH_FIELD_ORDER: {
  id: string;
  build: (pendingPolicyCount: number, policyOptionLabels?: string) => string;
}[] = [
    {
      id: "policyType",
      build: () =>
        "What type of policy is this? Use only the policy types on this customer's account.",
    },
    {
      id: "policyNumber",
      build: (pendingPolicyCount, policyOptionLabels) =>
        pendingPolicyCount > 1
          ? policyOptionLabels
            ? `There are ${pendingPolicyCount} policies for this type on the account: ${policyOptionLabels}. Read those policy numbers clearly, then ask for the first few or last few characters to identify which one.`
            : "There are multiple policies for this type. Read each policy number from the account, then ask for the first few or last few characters."
          : "Policy number is filled from the account — do not ask for it.",
    },
    {
      id: "locationType",
      build: () =>
        "What kind of location was this — at home, work, on the street, highway, or somewhere else?",
    },
    {
      id: "incidentType",
      build: () =>
        "What type of incident was this — for example fire, hail, or theft?",
    },
    {
      id: "date",
      build: () => "What date did the incident happen?",
    },
    {
      id: "time",
      build: () => "What time did the incident happen?",
    },
    {
      id: "incidentLocation",
      build: () =>
        "What is the incident location or address where this happened?",
    },
    {
      id: "incidentDescription",
      build: () => "Please briefly describe what happened in your own words.",
    },
  ];

/** Next question the voice agent should ask based on missing form fields. */
export function getNextMissingFieldCoach(
  fields: Record<string, ClaimField>,
  pendingPolicyCount = 0,
  policyOptionLabels = ""
): string | null {
  for (const step of COACH_FIELD_ORDER) {
    const field = fields[step.id];
    if (step.id === "policyNumber" && field?.status === "complete") {
      continue;
    }
    if (!field || field.status === "missing") {
      return step.build(pendingPolicyCount, policyOptionLabels);
    }
  }
  return null;
}

const CLAIM_FORM_ENQUIRY_LABELS: Record<string, string> = {
  policyType: "What type of policy is this?",
  policyNumber: "Which policy number applies to this claim?",
  locationType:
    "What kind of location was this — at home, at work, on the street, highway, or parking lot?",
  incidentType: "What type of incident was this?",
  date: "What date did the incident happen?",
  time: "What time did the incident happen?",
  incidentLocation: "What is the incident location or address where this happened?",
  incidentDescription: "Can you briefly describe what happened?",
};

/** CSR Enquires: intake questions still needed on the active claim form. */
export function getClaimFormEnquiryQuestions(
  fields: Record<string, ClaimField>
): string[] {
  const questions: string[] = [];
  for (const step of COACH_FIELD_ORDER) {
    const field = fields[step.id];
    if (step.id === "policyNumber" && field?.status === "complete") {
      continue;
    }
    if (!field || field.status === "missing") {
      const label = CLAIM_FORM_ENQUIRY_LABELS[step.id];
      if (label) questions.push(label);
    }
  }
  return questions;
}

/** Policy lookup only after caller spoke or agent clearly confirmed their answer. */
export function transcriptReadyForPolicyLookup(
  transcript: TranscriptTurn[]
): boolean {
  const hasClaimant = transcript.some(
    (t) => t.speaker === "Claimant" && t.text?.trim() && !t.streaming
  );
  if (hasClaimant) return true;
  return transcript.some(
    (t) =>
      t.speaker === "Agent" &&
      t.text?.trim() &&
      !t.streaming &&
      (/\bgot it,?\s*(?:homeowners?|home\s*owners?|personal\s+auto|auto)\b/i.test(
        t.text
      ) ||
        /\b(?:home\s*owner'?s?|homeowners?|(?:personal\s+)?auto)\s+policy\b/i.test(
          t.text
        ))
  );
}

function titleCaseWord(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function titleCaseWords(text: string): string {
  return text
    .trim()
    .split(/\s+/)
    .map(titleCaseWord)
    .join(" ");
}

const MONTH_NAMES =
  "january|february|march|april|may|june|july|august|september|october|november|december";

const SPOKEN_DAY_WORDS = [
  "first",
  "second",
  "third",
  "fourth",
  "fifth",
  "sixth",
  "seventh",
  "eighth",
  "ninth",
  "tenth",
  "eleventh",
  "twelfth",
  "thirteenth",
  "fourteenth",
  "fifteenth",
  "sixteenth",
  "seventeenth",
  "eighteenth",
  "nineteenth",
  "twentieth",
  "twenty-first",
  "twenty-second",
  "twenty-third",
  "twenty-fourth",
  "twenty-fifth",
  "twenty-sixth",
  "twenty-seventh",
  "twenty-eighth",
  "twenty-ninth",
  "thirtieth",
  "thirty-first",
].join("|");

const SPOKEN_DAY_TO_NUMBER: Record<string, number> = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
  sixth: 6,
  seventh: 7,
  eighth: 8,
  ninth: 9,
  tenth: 10,
  eleventh: 11,
  twelfth: 12,
  thirteenth: 13,
  fourteenth: 14,
  fifteenth: 15,
  sixteenth: 16,
  seventeenth: 17,
  eighteenth: 18,
  nineteenth: 19,
  twentieth: 20,
  "twenty-first": 21,
  "twenty-second": 22,
  "twenty-third": 23,
  "twenty-fourth": 24,
  "twenty-fifth": 25,
  "twenty-sixth": 26,
  "twenty-seventh": 27,
  "twenty-eighth": 28,
  "twenty-ninth": 29,
  thirtieth: 30,
  "thirty-first": 31,
};

function parseSpokenDayToken(raw: string): number | null {
  const token = raw.trim().toLowerCase().replace(/[,.]$/, "");
  const numeric = token.match(/^(\d{1,2})(?:st|nd|rd|th)?$/);
  if (numeric) {
    const day = parseInt(numeric[1], 10);
    return day >= 1 && day <= 31 ? day : null;
  }
  const twentyDay = token.match(
    /^twenty[- ]?(one|two|three|four|five|six|seven|eight|nine)$/
  );
  if (twentyDay) {
    const suffix: Record<string, number> = {
      one: 21,
      two: 22,
      three: 23,
      four: 24,
      five: 25,
      six: 26,
      seven: 27,
      eight: 28,
      nine: 29,
    };
    return suffix[twentyDay[1]] ?? null;
  }
  const day = SPOKEN_DAY_TO_NUMBER[token];
  return day ?? null;
}

function parseSpokenYearPhrase(raw: string): number | null {
  const token = raw.trim().toLowerCase().replace(/[,.]/g, "").replace(/\s+/g, " ");
  const numeric = token.match(/^(\d{4})$/);
  if (numeric) {
    const year = parseInt(numeric[1], 10);
    return year >= 1900 && year <= 2100 ? year : null;
  }

  const spokenYearMap: Record<string, number> = {
    "twenty twenty four": 2024,
    "twenty twenty-five": 2025,
    "twenty twenty five": 2025,
    "twenty twenty-six": 2026,
    "twenty twenty six": 2026,
    "twenty twenty-seven": 2027,
    "twenty twenty seven": 2027,
    "twenty twenty-eight": 2028,
    "twenty twenty eight": 2028,
    "two thousand twenty four": 2024,
    "two thousand twenty-five": 2025,
    "two thousand twenty five": 2025,
    "two thousand twenty-six": 2026,
    "two thousand twenty six": 2026,
    "two thousand twenty-seven": 2027,
    "two thousand twenty seven": 2027,
  };

  return spokenYearMap[token] ?? null;
}

function formatClockTime(hour12: number, minute: number, ampm: string): string {
  const normalizedAmpm = ampm.replace(/\./g, "").toUpperCase();
  return `${hour12}:${String(minute).padStart(2, "0")} ${normalizedAmpm}`;
}

/** e.g. 10:30 AM, ten thirty AM, at ten AM */
function extractTimeFromText(text: string): string | null {
  const trimmed = text.trim();

  const colon = trimmed.match(
    /\b(?:at\s+)?(\d{1,2}:\d{2}\s*(?:a\.?m\.?|p\.?m\.?))\b/i
  );
  if (colon) return colon[1].trim();

  const spokenHourWords =
    "one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve";
  const spokenMinuteWords =
    "oh|zero|five|ten|fifteen|twenty(?:[- ]?five)?|thirty|forty(?:[- ]?five)?|fifty";
  const spoken = trimmed.match(
    new RegExp(
      `\\b(?:at\\s+)?(${spokenHourWords})(?:\\s+(${spokenMinuteWords}))?\\s*(a\\.?m\\.?|p\\.?m\\.?)\\b`,
      "i"
    )
  );
  if (spoken) {
    const hourWords: Record<string, number> = {
      one: 1,
      two: 2,
      three: 3,
      four: 4,
      five: 5,
      six: 6,
      seven: 7,
      eight: 8,
      nine: 9,
      ten: 10,
      eleven: 11,
      twelve: 12,
    };
    const minuteWords: Record<string, number> = {
      oh: 0,
      zero: 0,
      five: 5,
      ten: 10,
      fifteen: 15,
      twenty: 20,
      "twenty-five": 25,
      "twenty five": 25,
      thirty: 30,
      forty: 40,
      "forty-five": 45,
      "forty five": 45,
      fifty: 50,
    };
    const hour = hourWords[spoken[1].toLowerCase()];
    if (!hour) return null;
    const minuteToken = spoken[2]?.toLowerCase().replace(/\s+/g, " ");
    const minute = minuteToken ? minuteWords[minuteToken] ?? 0 : 0;
    return formatClockTime(hour, minute, spoken[3]);
  }

  const aroundNumeric = trimmed.match(/\baround\s+(\d{1,2})\s*(a\.?m\.?|p\.?m\.?)\b/i);
  if (aroundNumeric) {
    return formatClockTime(
      parseInt(aroundNumeric[1], 10),
      0,
      aroundNumeric[2]
    );
  }

  const partOfDay = trimmed.match(
    /\b(?:this\s+|in\s+the\s+)?(morning|afternoon|evening|night)\b/i
  );
  if (partOfDay) {
    return titleCaseWord(partOfDay[1]);
  }

  return null;
}

/** e.g. April 2, 2026; April second, 2026; June, twenty twenty-six */
function extractDateFromText(text: string): string | null {
  const lower = text.toLowerCase();
  const monthFirst = lower.match(
    new RegExp(
      `(?:\\bon\\s+)?(${MONTH_NAMES})\\s+(\\d{1,2}(?:st|nd|rd|th)?|${SPOKEN_DAY_WORDS}|twenty[- ]?(?:one|two|three|four|five|six|seven|eight|nine)),?\\s*(\\d{4}|twenty\\s+twenty[- ]?(?:four|five|six|seven|eight)|two\\s+thousand\\s+twenty[- ]?(?:four|five|six|seven))\\b`,
      "i"
    )
  );
  if (monthFirst) {
    const day = parseSpokenDayToken(monthFirst[2]);
    const year = parseSpokenYearPhrase(monthFirst[3]);
    if (day && year) {
      return `${titleCaseWord(monthFirst[1])} ${day}, ${year}`;
    }
  }
  const dayFirst = lower.match(
    new RegExp(
      `(?:\\bon\\s+)?(\\d{1,2}(?:st|nd|rd|th)?|${SPOKEN_DAY_WORDS}|twenty[- ]?(?:one|two|three|four|five|six|seven|eight|nine))\\s+(${MONTH_NAMES}),?\\s*(\\d{4}|twenty\\s+twenty[- ]?(?:four|five|six|seven|eight)|two\\s+thousand\\s+twenty[- ]?(?:four|five|six|seven))\\b`,
      "i"
    )
  );
  if (dayFirst) {
    const day = parseSpokenDayToken(dayFirst[1]);
    const year = parseSpokenYearPhrase(dayFirst[3]);
    if (day && year) {
      return `${titleCaseWord(dayFirst[2])} ${day}, ${year}`;
    }
  }

  const monthSpokenYear = lower.match(
    new RegExp(
      `\\b(${MONTH_NAMES}),?\\s+(twenty\\s+twenty[- ]?(?:four|five|six|seven|eight)|two\\s+thousand\\s+twenty[- ]?(?:four|five|six|seven)|\\d{4})\\b`,
      "i"
    )
  );
  if (monthSpokenYear) {
    const year = parseSpokenYearPhrase(monthSpokenYear[2]);
    if (year) {
      return `${titleCaseWord(monthSpokenYear[1])}, ${year}`;
    }
  }

  const relative = lower.match(/\b(today|yesterday|day\s+before\s+yesterday)\b/i);
  if (relative) {
    const now = new Date();
    let d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const token = relative[1].toLowerCase().replace(/\s+/g, " ");
    if (token === "yesterday") d.setDate(d.getDate() - 1);
    else if (token === "day before yesterday") d.setDate(d.getDate() - 2);
    return formatJsLocalDate(d);
  }

  return null;
}

function formatJsLocalDate(d: Date): string {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** "this morning" fills time; if date missing, also treat as today. */
function applyPartOfDayDateTime(
  values: Record<string, string>,
  text: string
): void {
  if (!/\b(?:this\s+|in\s+the\s+)?(morning|afternoon|evening|night)\b/i.test(text)) {
    return;
  }
  if (!values.time) {
    const time = extractTimeFromText(text);
    if (time) values.time = time;
  }
  if (!values.date) {
    values.date = formatJsLocalDate(new Date());
  }
}

function compactNarrativeText(text: string, max = 220): string {
  const trimmed = text.trim().replace(/\s+/g, " ");
  return trimmed.length > max ? `${trimmed.slice(0, max - 3)}...` : trimmed;
}

function extractIncidentDescriptionFromNarrative(text: string): string | null {
  const clauses = [
    /\b(another\s+car[^.?!]{12,200}[.?!])/i,
    /\b((?:car|vehicle)\s+(?:ran\s+a\s+red\s+light\s+and\s+)?hit[^.?!]{8,160}[.?!])/i,
    /\b((?:our\s+)?(?:finished\s+)?basement[^.?!]{10,180}[.?!])/i,
    /\b((?:house|home)[^.?!]{0,40}(?:caught\s+on\s+fire|on\s+fire)[^.?!]{0,80}[.?!])/i,
    /\b((?:was\s+)?cooking[^.?!]{0,60}(?:fire|caught\s+on\s+fire)[^.?!]{0,80}[.?!])/i,
    /\b((?:was\s+)?stolen\s+from[^.?!]{10,160}[.?!])/i,
    /\b(flight\s+from[^.?!]{10,160}[.?!])/i,
  ];
  for (const pattern of clauses) {
    const match = text.match(pattern);
    if (match) return compactNarrativeText(match[1]);
  }
  const sentences = text.split(/(?<=[.!?])\s+/).filter((s) => s.trim().length >= 20);
  const accident = sentences.find((s) =>
    /\b(hit|crash|collision|red\s+light|ran\s+into|stolen|flood|totaled|accident)\b/i.test(s)
  );
  if (accident) return compactNarrativeText(accident);
  return null;
}

/** Parse long free-form claimant narratives (e.g. examples.py demos). */
function extractNarrativeFields(fullText: string): Record<string, string> {
  const values: Record<string, string> = {};
  const lower = fullText.toLowerCase();
  const trimmed = fullText.trim();
  if (trimmed.length < 40) return values;

  if (
    /\bauto\s+claim\b/i.test(lower) ||
    /\b(?:car|vehicle)\s+(?:accident|crash|collision)\b/i.test(lower) ||
    /\b(?:car|vehicle)\s+accident\b/i.test(lower) ||
    /\baccident\b.*\b(?:highway|car|vehicle|auto)\b/i.test(lower) ||
    /\b(?:highway|car|vehicle|auto)\b.*\baccident\b/i.test(lower) ||
    /\b(?:my\s+)?auto\s+policy\b/i.test(lower) ||
    /\brear[- ]?ended\b/i.test(lower) ||
    /\banother\s+car\b/i.test(lower) ||
    /\bred\s+light\b/i.test(lower)
  ) {
    values.policyType = "Auto";
  } else if (
    /\bhomeowners?\s+claim\b/i.test(lower) ||
    /\b(?:basement|sump\s+pump)\b/i.test(lower) ||
    /\b(?:home|house|property)\s+(?:flood|damage)\b/i.test(lower)
  ) {
    values.policyType = "Homeowners";
  } else if (/\btravel\s+claim\b/i.test(lower)) {
    values.policyType = "Travel";
  }

  const policyFromNarrative = extractPolicyNumberFromText(trimmed);
  if (policyFromNarrative) {
    values.policyNumber = policyFromNarrative;
  }

  const extractedDate = extractDateFromText(trimmed);
  if (extractedDate) values.date = extractedDate;

  const extractedTime = extractTimeFromText(trimmed);
  if (extractedTime) values.time = extractedTime;
  applyPartOfDayDateTime(values, trimmed);

  const nearInCity = trimmed.match(
    /\bnear\s+([^,.]+?)\s+in\s+([A-Za-z][A-Za-z\s.-]{2,28})(?=\s*[,.]|\s+(?:another|my|the|on)\b)/i
  );
  if (nearInCity) {
    const loc = `${nearInCity[1].trim()} in ${titleCaseWords(nearInCity[2])}`;
    if (isPlausibleIncidentLocation(loc)) values.incidentLocation = loc;
  } else {
    const atInCity = trimmed.match(
      /\bat\s+(?:a\s+)?([^,.]+?)\s+in\s+([A-Za-z][A-Za-z\s.-]{2,28})(?=\s+on\s+[A-Za-z]|\s*[,.])/i
    );
    if (atInCity) {
      const loc = `${atInCity[1].trim()} in ${titleCaseWords(atInCity[2])}`;
      if (isPlausibleIncidentLocation(loc)) values.incidentLocation = loc;
    } else {
      const placeInCity = trimmed.match(
        /\b(?:basement|home|house|shop|property)\s+in\s+([A-Za-z][A-Za-z\s.-]{2,28})(?=\s+(?:flood|on|around)\b)/i
      );
      if (placeInCity) {
        const city = titleCaseWords(placeInCity[1]);
        if (isPlausibleIncidentLocation(city)) values.incidentLocation = city;
      }
    }
  }

  if (/\b(?:ran\s+a\s+red\s+light|red\s+light|hit\s+my|hit\s+(?:the\s+)?driver|hit\s+me|from\s+behind|another\s+car|rear[- ]?ended|t-boned)\b/i.test(lower)) {
    values.incidentType = "Collision";
  } else if (/\b(?:car|vehicle)\s+accident\b/i.test(lower)) {
    values.incidentType = "Collision";
  } else if (/\bcollision\b/i.test(lower)) {
    values.incidentType = "Collision";
  } else if (/\b(?:stolen|theft)\b/i.test(lower)) {
    values.incidentType = "Theft";
  } else if (/\b(?:flood(?:ed|ing)?|sump\s+pump)\b/i.test(lower)) {
    values.incidentType = "Flood";
  } else if (/\bfire\b/i.test(lower)) {
    values.incidentType = "Fire";
  }

  if (/\b(?:intersection|red\s+light|and\s+\w+\s+and\s+)/i.test(lower) || /\bnear\s+[ns][ew]?\s*\d/i.test(trimmed)) {
    values.locationType = "Street";
  } else if (/\b(?:highway|interstate|freeway|mile marker)\b/i.test(lower)) {
    values.locationType = "Highway";
  } else if (
    !/\b(?:auto,\s*home|home,\s*or)\b/i.test(lower) &&
    /\b(?:basement|sump pump|at\s+home|our\s+home|at\s+the\s+residence)\b/i.test(lower)
  ) {
    values.locationType = "Residential";
  } else if (
    /\b(?:on my way to work|going to work|commut(?:e|ing) to work|way to work)\b/i.test(
      lower
    ) ||
    /\b(?:coffee\s+shop|store|workplace|at\s+work)\b/i.test(lower)
  ) {
    values.locationType = "Workplace";
  } else if (/\bparking\s+lot\b/i.test(lower)) {
    values.locationType = "Parking lot";
  }

  if (!values.policyType && /\b(personal\s*auto|auto\s+policy)\b/i.test(lower)) {
    values.policyType = "Auto";
  }
  if (
    !values.policyType &&
    (/\bhome\s*owners?\b/i.test(lower) ||
      /\bhomeowners?\s+policy\b/i.test(lower) ||
      /\b(?:your\s+)?home,?\s*(?:got it|okay|ok|understood)\b/i.test(lower) ||
      /\b(?:in\s+)?my\s+home\b/i.test(lower) ||
      (/\bfire\b/i.test(lower) && /\b(?:home|house|cooking)\b/i.test(lower)))
  ) {
    values.policyType = "Homeowners";
  }

  if (!values.locationType && /\bon\s+(?:the\s+)?street\b/i.test(lower)) {
    values.locationType = "Street";
  }
  if (
    !values.locationType &&
    !/\b(?:auto,\s*home|home,\s*or\s+renters|for example\s+auto)\b/i.test(lower) &&
    /\b(?:at\s+(?:my\s+)?home|my\s+home|at\s+home|residence|at\s+the\s+residence)\b/i.test(
      lower
    )
  ) {
    values.locationType = "Residential";
  }

  if (values.incidentLocation && !isPlausibleIncidentLocation(values.incidentLocation)) {
    delete values.incidentLocation;
  }

  return values;
}

/** Parse one transcript turn; later turns overwrite earlier ones. */
export function extractFromTurn(
  text: string,
  speaker: string,
  policyTypeHint = ""
): Record<string, string> {
  const values: Record<string, string> = {};
  const trimmed = text.trim();
  if (!trimmed) return values;
  const lower = trimmed.toLowerCase();
  const isAgent = speaker === "Agent";

  if (isAgent) {
    return extractAgentConfirmation(trimmed);
  }

  if (/\bfor\s+example\b/i.test(lower)) {
    return values;
  }

  if (
    /\b(?:car|vehicle)\s+accident\b/i.test(lower) ||
    /\baccident\b.*\b(?:highway|car|vehicle|auto)\b/i.test(lower) ||
    /\brear[- ]?ended\b/i.test(lower)
  ) {
    values.policyType = values.policyType || "Auto";
  }

  if (
    /\bpersonal\s+auto\b/i.test(trimmed) ||
    /\b(?:my\s+)?auto\s+policy\b/i.test(lower) ||
    /\bgot it,?\s*an?\s+auto\s+policy\b/i.test(lower)
  ) {
    values.policyType = "Auto";
  } else if (/\b(dwelling|dp-?3)\b/i.test(lower)) {
    values.policyType = "Dwelling";
  } else if (/\bpersonal\s+auto\b/i.test(lower)) {
    values.policyType = "Personal Auto";
  } else if (
    /\bhome\s*owners?\b/i.test(trimmed) ||
    /\bhomeowners?\b/i.test(trimmed) ||
    /^homeowners?\.?$/i.test(trimmed) ||
    /\bhome\s+policy\b/i.test(lower) ||
    /\bhomeowners?\s+policy\b/i.test(lower) ||
    /\b(?:your\s+)?home,?\s*(?:got it|okay|ok|understood|thank you)\b/i.test(trimmed) ||
    /^home\.?$/i.test(trimmed)
  ) {
    values.policyType = "Homeowners";
  } else if (
    /\b(?:an?\s+)?auto(?:\s+policy)?,?\s*(?:got it|okay|ok|understood|thank you)\b/i.test(
      trimmed
    ) ||
    /^auto\.?$/i.test(trimmed)
  ) {
    values.policyType = "Auto";
  } else if (
    speaker === "Claimant" &&
    trimmed.length <= 24 &&
    /\bcommercial\b/i.test(lower) &&
    !isPlausibleIncidentLocation(trimmed)
  ) {
    values.policyType = "Commercial";
  }

  const policyHave = trimmed.match(
    /\b(?:thanks,?\s*)?i have\s+([A-Z]{2}\d{3,})\b/i
  );
  if (policyHave) {
    values.policyNumber = policyHave[1].toUpperCase();
  } else if (
    speaker === "Claimant" &&
    !/\b(?:policy\s+number|your\s+policy\s+number)\b/i.test(lower)
  ) {
    const policyCode = trimmed.match(/\b([A-Z]{2}\d{4,})\b/);
    if (policyCode) {
      values.policyNumber = policyCode[1].toUpperCase();
    }
  }

  if (
    /\b(?:on my way to work|going to work|commut(?:e|ing) to work|way to work)\b/i.test(
      lower
    ) ||
    /\bat work,?\s*(?:ok(?:ay)?)?\b/i.test(trimmed) ||
    /^at\s+(?:my\s+)?work(?:place)?\.?$/i.test(lower) ||
    /^workplace\.?$/i.test(lower)
  ) {
    values.locationType = "Workplace";
  } else if (
    /\bat home,?\s*(?:ok(?:ay)?)?\b/i.test(trimmed) ||
    /^at\s+(?:my\s+)?(?:home|house|residence)\.?$/i.test(lower) ||
    /\bprivate\s+propert/i.test(lower) ||
    /\bresidential\b/i.test(lower) ||
    /^private\.?$/i.test(lower)
  ) {
    values.locationType = "Residential";
  } else if (
    !/\b(?:auto,\s*home|home,\s*or)\b/i.test(lower) &&
    /\b(?:at\s+(?:my\s+)?home|my\s+home|residence|at\s+the\s+residence)\b/i.test(lower)
  ) {
    values.locationType = "Residential";
  } else if (
    /\bpublic\s+(?:r(?:oa)?d|propert)/i.test(lower) ||
    /^public\.?$/i.test(lower)
  ) {
    values.locationType = "Public road";
  } else if (/\b(?:highway|interstate|freeway)\b/i.test(lower)) {
    values.locationType = "Highway";
  } else if (
    /\bparking\s+lot\b/i.test(lower) ||
    /^parking\.?$/i.test(lower)
  ) {
    values.locationType = "Parking lot";
  } else if (
    !/at home, at work, or somewhere else/i.test(trimmed) &&
    !/\bfor\s+example\b/i.test(lower) &&
    (/\b(?:on\s+the\s+)?street,?\s*(?:okay|ok|got it|understood)\b/i.test(trimmed) ||
      (/^(?:on\s+)?(?:the\s+)?street\.?$/i.test(trimmed) && !/\?/.test(trimmed)))
  ) {
    values.locationType = "Street";
  }

  const skipIncidentTurn =
    /\bfor\s+example\b/i.test(lower) ||
    (/\?/.test(trimmed) &&
      /\b(highway|parking|workplace|or other|something else|fire|hail|theft)\b/i.test(
        lower
      ));
  if (!skipIncidentTurn) {
    const incidentMatch = matchIncidentTypeFromSpeech(trimmed, policyTypeHint);
    if (incidentMatch && incidentMatch !== "Wind and other") {
      values.incidentType = incidentMatch;
    } else if (/\ba\s+collision\b/i.test(lower) || /^collision\.?$/i.test(trimmed)) {
      values.incidentType = "Collision";
    }
  }

  if (!values.incidentType) {
    const inferred = inferIncidentTypeFromNarrative(
      trimmed,
      policyTypeHint || values.policyType
    );
    if (inferred) values.incidentType = inferred;
  }

  const extractedDate = extractDateFromText(trimmed);
  if (extractedDate) values.date = extractedDate;

  const extractedTime = extractTimeFromText(trimmed);
  if (extractedTime) values.time = extractedTime;
  applyPartOfDayDateTime(values, trimmed);

  const streetDenver = trimmed.match(
    /street\s+number\s+(\d+)\s+in\s+([A-Za-z]+)/i
  );
  if (streetDenver) {
    values.incidentLocation = `Street number ${streetDenver[1]} in ${titleCaseWord(streetDenver[2])}`;
  } else {
    const streetLoc = lower.match(
      /\bstreet\s+number\s+(\d+)\s+(?:in\s+)?([a-z][a-z\s]{1,24})/
    );
    if (streetLoc) {
      const place = streetLoc[2]
        .trim()
        .split(/\s+/)
        .map(titleCaseWord)
        .join(" ");
      values.incidentLocation = `Street number ${streetLoc[1]} in ${place}`;
    } else if (/\bdenver\b/i.test(lower)) {
      values.incidentLocation = "Denver";
    } else if (
      speaker === "Claimant" &&
      !looksLikePolicyNumberSpeech(trimmed) &&
      isPlausibleIncidentLocation(trimmed) &&
      !isNarrativeOrDatetimeSpeech(trimmed)
    ) {
      values.incidentLocation = trimmed.replace(/\.$/, "").trim();
    }
  }

  if (/\b(?:house|home).{0,40}caught\s+on\s+fire\b/i.test(lower) || /\bcaught\s+on\s+fire\b/i.test(lower)) {
    values.incidentDescription =
      extractIncidentDescriptionFromNarrative(trimmed) || "House caught on fire";
  } else if (/\bran\s+into\s+another\s+car\b/i.test(lower)) {
    values.incidentDescription = "Ran into another car";
  } else if (/\byou\s+ran\s+into\s+another\s+car\b/i.test(lower)) {
    values.incidentDescription = "You ran into another car";
  } else if (/\banother\s+car\s+(?:hit|ran\s+into)\b/i.test(lower)) {
    values.incidentDescription = compactText(trimmed);
  } else if (/\bcar\s+ran\s+into\b/i.test(lower)) {
    values.incidentDescription = "A car ran into yours";
  }

  const turnDescription = extractDescriptionFromSingleTurn(trimmed);
  if (turnDescription) values.incidentDescription = turnDescription;

  if (speaker === "Claimant" && trimmed.length >= 80) {
    const narrative = extractNarrativeFields(trimmed);
    for (const [key, value] of Object.entries(narrative)) {
      if (key === "incidentDescription") continue;
      if (value) values[key] = value;
    }
  }

  return values;
}

function applyTranscriptHighlights(
  merged: Record<string, string>,
  fullText: string
): Record<string, string> {
  const out = { ...merged };
  const narrative = extractNarrativeFields(fullText);
  for (const [key, value] of Object.entries(narrative)) {
    if (!value || isWeakFieldValue(value)) continue;
    if (key === "incidentDescription") continue;
    if (key === "incidentLocation" && !isPlausibleIncidentLocation(value)) continue;
    out[key] = value;
  }

  const streetDenver = fullText.match(
    /street\s+number\s+(\d+)\s+in\s+([A-Za-z]+)/i
  );
  if (streetDenver) {
    out.incidentLocation = `Street number ${streetDenver[1]} in ${titleCaseWord(streetDenver[2])}`;
  } else if (/\bdenver\b/i.test(fullText.toLowerCase()) && !out.incidentLocation) {
    out.incidentLocation = "Denver";
  }

  return out;
}

function agentAskedClaimFieldBefore(
  ordered: TranscriptTurn[],
  beforeIdx: number,
  fieldId: string
): boolean {
  const patterns: Record<string, RegExp> = {
    incidentLocation:
      /\b(?:where|location|address|incident location|happened|city|town)\b/i,
    policyType: /\b(?:type of policy|what policy|which policy|policy are you calling)\b/i,
    policyNumber:
      /\b(?:policy number|first few letters|last few characters|which policy)\b/i,
  };
  const pattern = patterns[fieldId];
  if (!pattern) return true;

  for (let i = beforeIdx - 1; i >= 0; i -= 1) {
    const turn = ordered[i];
    if (turn.speaker === "Claimant") return false;
    if (turn.speaker === "Agent" && pattern.test(turn.text || "")) return true;
  }
  return false;
}

export function extractFieldsFromTranscript(
  turns: TranscriptTurn[]
): Record<string, string> {
  const merged: Record<string, string> = {};
  const ordered = turns.filter((t) => t.text?.trim() && !t.streaming);
  const claimantTurns = ordered.filter((t) => t.speaker === "Claimant");

  for (const turn of claimantTurns) {
    const turnIdx = ordered.indexOf(turn);
    const partial = extractFromTurn(
      turn.text,
      "Claimant",
      merged.policyType || ""
    );
    for (const [key, value] of Object.entries(partial)) {
      if (!value || isWeakFieldValue(value)) continue;
      if (key === "incidentLocation") {
        if (!isPlausibleIncidentLocation(value)) continue;
        if (!shouldOverwriteIncidentLocation(merged.incidentLocation, value)) {
          continue;
        }
        if (!agentAskedClaimFieldBefore(ordered, turnIdx, "incidentLocation")) {
          continue;
        }
      }
      if (
        key === "policyType" &&
        !agentAskedClaimFieldBefore(ordered, turnIdx, "policyType") &&
        /\b(file\s+a\s+claim|add\s+a\s+(?:driver|vehicle))\b/i.test(turn.text || "")
      ) {
        continue;
      }
      merged[key] = value;
    }
  }

  let claimantPolicyType = merged.policyType || "";
  if (claimantPolicyType) {
    for (const turn of turns) {
      if (turn.speaker !== "Agent" || !turn.text?.trim() || turn.streaming) {
        continue;
      }
      const agentConfirm = extractAgentConfirmation(turn.text);
      if (agentConfirm.policyType) {
        merged.policyType = agentConfirm.policyType;
        claimantPolicyType = agentConfirm.policyType;
      }
    }
  }

  const claimantText = turns
    .filter((t) => t.speaker === "Claimant" && t.text?.trim() && !t.streaming)
    .map((t) => t.text)
    .join(" ");
  if (claimantText.trim().length >= 25) {
    const highlighted = applyTranscriptHighlights(merged, claimantText);
    if (claimantPolicyType) highlighted.policyType = claimantPolicyType;
    else if (highlighted.policyType) claimantPolicyType = highlighted.policyType;
    const description = extractDescriptionFromClaimantTurns(ordered);
    if (description) highlighted.incidentDescription = description;
    return highlighted;
  }
  const description = extractDescriptionFromClaimantTurns(ordered);
  if (description) merged.incidentDescription = description;
  if (claimantPolicyType) merged.policyType = claimantPolicyType;
  return merged;
}

function applyExtractedFieldsToState(
  state: IntakeState,
  extracted: Record<string, string>
): IntakeState {
  if (!Object.keys(extracted).length) return state;
  const nextFields = { ...state.fields };
  let changed = false;
  for (const [id, value] of Object.entries(extracted)) {
    if (!value || !FIELD_LABELS[id] || isWeakFieldValue(value)) continue;
    const current = nextFields[id];
    if (current?.value === value) {
      if (current.status === "pending") {
        nextFields[id] = { ...current, status: "complete" };
        changed = true;
      }
      continue;
    }
    nextFields[id] = {
      label: FIELD_LABELS[id],
      value,
      status: "complete",
      source: "live transcript",
    };
    changed = true;
  }
  if (!changed) return state;
  const completed = Object.values(nextFields).filter(
    (field) => field.status !== "missing"
  ).length;
  return {
    ...state,
    fields: nextFields,
    progress: Math.max(
      state.progress || 0,
      Math.round((completed / Object.keys(nextFields).length) * 100)
    ),
  };
}

export function applyHintsFromFullTranscript(state: IntakeState): IntakeState {
  if (!transcriptHasClaimantAnswer(state.transcript)) {
    return state;
  }
  const extracted = extractFieldsFromTranscript(state.transcript);
  return applyInferredIncidentType(applyExtractedFieldsToState(state, extracted));
}

function applyInferredIncidentType(state: IntakeState): IntakeState {
  if (!isMissingField(state.fields.incidentType)) return state;

  const policyType = state.fields.policyType?.value?.trim() || "Auto";
  let source = state.fields.incidentDescription?.value?.trim() || "";
  if (!source || /^missing:/i.test(source)) {
    source = state.transcript
      .filter((t) => t.speaker === "Claimant" && t.text?.trim())
      .map((t) => t.text!)
      .join(" ");
  }

  const inferred = inferIncidentTypeFromNarrative(source, policyType);
  if (!inferred) return state;

  return setClaimFieldValue(
    state,
    "incidentType",
    inferred,
    "complete",
    "inferred from description"
  );
}

/** Seed claim fields from what the caller already said when FNOL starts mid-conversation. */
export function bootstrapClaimStateFromTranscript(
  apiOrigin: string,
  transcript: TranscriptTurn[],
  accountPolicyLabels: string[] = []
): IntakeState {
  const empty = buildEmptyState(apiOrigin);
  let state = applyHintsFromFullTranscript({ ...empty, transcript });

  if (isMissingField(state.fields.policyType) && accountPolicyLabels.length === 1) {
    state = setClaimFieldValue(
      state,
      "policyType",
      accountPolicyLabels[0],
      "complete",
      "account"
    );
  }

  if (isMissingField(state.fields.policyType)) {
    const claimantText = transcript
      .filter((t) => t.speaker === "Claimant" && t.text?.trim())
      .map((t) => t.text!)
      .join(" ");
    const autoLabel =
      accountPolicyLabels.find((label) => /auto/i.test(label)) || "Personal Auto";
    if (
      /\b(?:car|vehicle)\s+accident\b/i.test(claimantText) ||
      /\baccident\b.*\b(?:highway|car|vehicle|auto)\b/i.test(claimantText) ||
      /\b(?:highway|car|vehicle|auto)\b.*\baccident\b/i.test(claimantText) ||
      /\b(?:my\s+)?auto\s+policy\b/i.test(claimantText) ||
      /\brear[- ]?ended\b/i.test(claimantText)
    ) {
      state = setClaimFieldValue(
        state,
        "policyType",
        autoLabel,
        "complete",
        "inferred from incident"
      );
    }
  }

  return applyInferredIncidentType(state);
}

export function setClaimFieldValue(
  state: IntakeState,
  id: string,
  value: string,
  status: ClaimField["status"] = "complete",
  source = "customer policies"
): IntakeState {
  if (!FIELD_LABELS[id]) return state;
  return {
    ...state,
    fields: {
      ...state.fields,
      [id]: {
        label: FIELD_LABELS[id],
        value,
        status,
        source,
      },
    },
  };
}

export function normalizeServerState(state: IntakeState): IntakeState {
  const merged: Record<string, ClaimField> = {
    ...buildEmptyState(getVoiceApiOrigin()).fields,
  };

  const hasClaimant = transcriptHasClaimantAnswer(state.transcript);
  const claimantLines = state.transcript
    .filter((t) => t.speaker === "Claimant" && t.text?.trim())
    .map((t) => t.text!.trim());
  for (const [id, field] of Object.entries(state.fields || {})) {
    if (!hasClaimant && field?.source === "live transcript") continue;
    const targetId = LEGACY_FIELD_IDS[id] || id;
    mergeIncomingField(merged, targetId, field, claimantLines);
  }

  const incident = merged.incidentType;
  if (isMissingField(merged.policyType) && incident && !isMissingField(incident)) {
    const incidentText = incident.value.toLowerCase();
    if (incidentText.includes("auto") || incidentText.includes("collision")) {
      merged.policyType = {
        label: "Policy Type",
        value: "Auto",
        status: "complete",
        source: "inferred",
      };
    } else if (incidentText.includes("water") || incidentText.includes("home")) {
      merged.policyType = {
        label: "Policy Type",
        value: "Home",
        status: "complete",
        source: "inferred",
      };
    }
  }

  const withHints = applyHintsFromFullTranscript({ ...state, fields: merged });
  const finalFields = mergeClaimFields(merged, withHints.fields, claimantLines);
  const transcriptDescription = extractDescriptionFromClaimantTurns(
    state.transcript
  );
  if (transcriptDescription) {
    const current = finalFields.incidentDescription;
    if (
      !current ||
      isMissingField(current) ||
      isBadIncidentDescription(current.value, claimantLines)
    ) {
      finalFields.incidentDescription = {
        label: FIELD_LABELS.incidentDescription,
        value: transcriptDescription,
        status: "complete",
        source: "live transcript",
      };
    }
  }

  if (isMissingField(finalFields.incidentType)) {
    const policyType = finalFields.policyType?.value?.trim() || "Auto";
    const description =
      finalFields.incidentDescription?.value?.trim() || transcriptDescription || "";
    const inferred = inferIncidentTypeFromNarrative(description, policyType);
    if (inferred) {
      finalFields.incidentType = {
        label: FIELD_LABELS.incidentType,
        value: inferred,
        status: "complete",
        source: "inferred from description",
      };
    }
  }

  const filledCount = Object.values(finalFields).filter(
    (f) => f.status !== "missing"
  ).length;
  const progress = Math.max(
    state.progress || 0,
    Math.round((filledCount / Object.keys(finalFields).length) * 100)
  );
  return { ...withHints, fields: finalFields, progress };
}

export function buildEmptyState(apiOrigin: string): IntakeState {
  return {
    route: "needs_docs",
    progress: 0,
    fields: Object.fromEntries(
      INITIAL_FIELD_GROUPS.flatMap((group) =>
        group.fields.map(([id, label]) => [
          id,
          {
            label,
            value: `Missing: ${label.toLowerCase()}`,
            status: "missing",
            source: "-",
          },
        ])
      )
    ),
    transcript: [],
    events: [
      {
        tone: "warning",
        title: "Connecting",
        detail: `Waiting for ${apiOrigin}/api/sessions.`,
        rule: "API-000",
      },
    ],
    handoff: {
      Summary: "Backend session not started yet.",
      Priority: "Pending",
      "Required actions": "Start a live intake session.",
      Attachments: "None",
      "Next best action": "Connect to the backend API.",
    },
    packet_markdown:
      "# Initial Adjuster Handoff\n\nWaiting for backend session.",
  };
}

export function sameTranscriptTurn(
  left: TranscriptTurn,
  right: TranscriptTurn
): boolean {
  const leftText = String(left?.text || "").trim();
  const rightText = String(right?.text || "").trim();
  if (!leftText || !rightText || left?.speaker !== right?.speaker) return false;
  return (
    leftText === rightText ||
    leftText.includes(rightText) ||
    rightText.includes(leftText)
  );
}

export function mergeLiveTranscript(
  authoritative: TranscriptTurn[],
  local: TranscriptTurn[]
): TranscriptTurn[] {
  const merged = [...(authoritative || [])];
  for (const turn of local || []) {
    if (!turn.streaming || !String(turn.text || "").trim()) continue;
    if (merged.some((item) => sameTranscriptTurn(item, turn))) continue;
    merged.push(turn);
  }
  return merged;
}

export function splitList(value: string): string[] {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export interface OperatorGuidance {
  routeLabel: string;
  routeTone: string;
  priority: string;
  nextAction: string;
  missingItems: string[];
  readinessCopy: string;
  auditEvents: ClaimEvent[];
}

export function buildOperatorGuidance(state: IntakeState): OperatorGuidance {
  const route = state.route || "needs_docs";
  const handoff = state.handoff || {};
  const missingEvent = [...(state.events || [])]
    .reverse()
    .find(
      (event) =>
        event.rule === "INTAKE-001" && event.title === "Missing intake facts"
    );
  const missingItems = missingEvent?.detail
    ? missingEvent.detail.split(",").map((item) => item.trim()).filter(Boolean)
    : Object.values(state.fields || {})
        .filter((field) => field.status === "missing")
        .map((field) => field.label)
        .slice(0, 6);
  const requiredActions = splitList(handoff["Required actions"]);
  const routeTone =
    route === "emergency_escalation"
      ? "danger"
      : route === "ready_for_adjuster"
        ? "success"
        : "warning";
  const readinessCopy =
    route === "ready_for_adjuster"
      ? "Core intake is ready for assignment."
      : requiredActions.length
        ? `Collect or confirm: ${requiredActions.slice(0, 2).join(", ")}.`
        : "Continue collecting the highlighted intake facts.";
  return {
    routeLabel: ROUTE_LABELS[route] || route,
    routeTone,
    priority: handoff.Priority || "Waiting for claim facts.",
    nextAction:
      handoff["Next best action"] || "Ask for the next missing intake fact.",
    missingItems: missingItems.length
      ? missingItems
      : ["No blocking intake items"],
    readinessCopy,
    auditEvents: state.events || [],
  };
}

function compactText(text: string): string {
  const trimmed = text.trim().replace(/\s+/g, " ");
  return trimmed.length > 96 ? `${trimmed.slice(0, 93)}...` : trimmed;
}

function stripNegatedSafety(text: string): string {
  return text
    .replace(
      /\b(?:no|not|none|without|denies|denied)\s+(?:one\s+)?(?:was\s+)?(?:injur\w*|hurt|pain|medical attention|ambulance|hospital|unsafe|hazard\w*|danger)\b/gi,
      " "
    )
    .replace(
      /\b(?:injur\w*|hurt|pain|medical attention|ambulance|hospital|unsafe|hazard\w*|danger)\s+(?:was|were|is|are)?\s*(?:reported\s+)?(?:no|none|not reported|denied)\b/gi,
      " "
    );
}

/** Live chunk hints — only parses the current turn, not the full transcript. */
export function applyRealtimeHints(
  state: IntakeState,
  text: string,
  speaker = "Claimant"
): IntakeState | null {
  const lower = text.toLowerCase();
  const fromTurn = applyExtractedFieldsToState(
    state,
    extractFromTurn(text, speaker)
  );
  if (fromTurn !== state) return fromTurn;

  const safetyText = stripNegatedSafety(lower);
  if (
    !/\b(neck pain|injur|hurt|ambulance|hospital|urgent care|bleeding|pain)\b/.test(
      safetyText
    )
  ) {
    return null;
  }

  const nextFields = { ...state.fields };
  const nextEvents = [...state.events];
  const key = "SAFE-002:Injury signal detected";
  if (!nextEvents.some((item) => `${item.rule}:${item.title}` === key)) {
    nextEvents.push({
      rule: "SAFE-002",
      title: "Injury signal detected",
      detail: "Live audio mentions injury or medical concern.",
      tone: "danger",
      time: nowTime(),
    });
  }
  nextFields.incidentDescription = {
    ...nextFields.incidentDescription,
    label: FIELD_LABELS.incidentDescription,
    value: compactText(text),
    status: "urgent",
    source: "live audio",
  };
  return {
    ...state,
    route: "emergency_escalation",
    fields: nextFields,
    events: nextEvents.slice(-14),
  };
}

export function claimantAskedToClose(text: string): boolean {
  return /\b(that'?s all|nothing else|no,?\s*that'?s it|that is it|i'?m done|goodbye|bye)\b/i.test(
    text
  );
}

export function agentClosedConversation(text: string): boolean {
  return /\b(have a good|adjuster will|will be in touch|claim packet|initial intake|next steps)\b/i.test(
    text
  );
}

/** Update a plain transcript list (used by voice form orchestrator). */
export function upsertTranscriptTurnList(
  transcript: TranscriptTurn[],
  speaker: string,
  text: string,
  final = false
): TranscriptTurn[] {
  const shell: IntakeState = {
    route: "",
    progress: 0,
    fields: {},
    transcript,
    events: [],
    handoff: {},
  };
  const next = upsertStreamingTurn(shell, speaker, text, final);
  return next?.transcript ?? transcript;
}

export function upsertStreamingTurn(
  state: IntakeState,
  speaker: string,
  text: string,
  final = false
): IntakeState | null {
  if (!String(text || "").trim()) return null;
  const transcript = [...state.transcript];
  const last = transcript[transcript.length - 1];
  if (last && last.speaker === speaker && last.streaming) {
    transcript[transcript.length - 1] = {
      speaker,
      text,
      time: last.time,
      streaming: !final,
    };
  } else if (
    final &&
    last &&
    !last.streaming &&
    sameTranscriptTurn(last, { speaker, text })
  ) {
    return null;
  } else {
    transcript.push({ speaker, text, time: nowTime(), streaming: !final });
  }
  return { ...state, transcript };
}

export function resampleToPcm16(
  input: Float32Array,
  inputRate: number,
  outputRate: number
): Int16Array {
  const ratio = inputRate / outputRate;
  const outputLength = Math.floor(input.length / ratio);
  const output = new Float32Array(outputLength);
  for (let i = 0; i < outputLength; i += 1) {
    const index = i * ratio;
    const before = Math.floor(index);
    const after = Math.min(before + 1, input.length - 1);
    const weight = index - before;
    output[i] = input[before] * (1 - weight) + input[after] * weight;
  }
  const pcm = new Int16Array(output.length);
  for (let i = 0; i < output.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, output[i]));
    pcm[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return pcm;
}

export function arrayBufferToBase64(buffer: ArrayBuffer | ArrayBufferView): string {
  const bytes =
    buffer instanceof ArrayBuffer
      ? new Uint8Array(buffer)
      : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToInt16Array(base64: string): Int16Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer);
}
